<script>
import { CHART_COLORS } from '@/javascript/helpers/chartColors';
import { CalendarView, CalendarViewHeader } from "vue-simple-calendar";

export default {
  name: "CampaignCalendar",
  components: { CalendarView, CalendarViewHeader },
  props: {
    campaigns: { type: Array, default: () => [] },
    holidays: { type: Array, default: () => [] },
    scopeStart: { type: String, default: null },
    scopeEnd: { type: String, default: null },
    year: { type: Number, required: true },
    month: { type: Number, required: true },
    loaded: { type: Boolean, default: false },
    error: { type: Boolean, default: false }
  },
  emits: ['navigate', 'today', 'selectCampaign'],
  data() {
    //Shows what's on the calendar
    return { hasLoadedOnce: false, hoveredCampaign: null, tooltipPos: { x: 0, y: 0 }, displayYear: this.year, displayMonth: this.month, displayCampaigns: this.campaigns, displayHolidays: this.holidays };
  },
  computed: {
    showDate() {
      return new Date(this.displayYear, this.displayMonth - 1, 1);
    },
    colors() {
      return [CHART_COLORS.chart.green, CHART_COLORS.secondary, CHART_COLORS.chart.blue, CHART_COLORS.chart.purple, CHART_COLORS.chart.yellow, CHART_COLORS.chart.teal];
    },
    calendarItems() {
      //Calendar will show up to 6 (like the old one)
      return this.displayCampaigns.slice(0, 6).map((c, i) => ({
        id: c.id,
        startDate: c.start_date,
        endDate: c.end_date || new Date(this.displayYear, this.displayMonth, 0).toISOString().slice(0, 10),
        title: c.name,
        classes: [`campaign-color-${i}`]
      }));
    },
    campaignColorMap() {
      const map = {};
      this.campaigns.slice(0, 6).forEach((c, i) => { map[c.id] = i; });
      return map;
    },
    weeksInMonth() {
      const first = new Date(this.displayYear, this.displayMonth - 1, 1);
      const last = new Date(this.displayYear, this.displayMonth, 0);
      const startDay = first.getDay();
      const totalDays = last.getDate();
      return Math.ceil((startDay + totalDays) / 7);
    },
    weekRowHeight() {
      return this.weeksInMonth >= 6 ? '4.2em' : '5em';
    },

    // Holidays
    holidayDateClasses() {
      const classes = {};
      this.displayHolidays.forEach(h => {
        const date = h.date;
        classes[date] = 'cal-holiday';
      });
      return classes;
    },
    holidayMap() {
      const map = {};
      this.displayHolidays.forEach(h => {
        map[h.date] = h.name;
      });
      return map;
    },
    calendarCssVars() {
      const vars = {};
      this.colors.forEach((c, i) => { vars[`--campaign-color-${i}`] = c; });
      return vars;
    },
    //Finds max number of campaigns in a week (this is used to help determine the height of the weeks)
    maxOverlap() {
      const items = this.calendarItems;
      if (!items.length) return 1;
      const first = new Date(this.displayYear, this.displayMonth - 1, 1);
      const last = new Date(this.displayYear, this.displayMonth, 0);
      let max = 1;
      let weekStart = new Date(first);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      while (weekStart <= last) {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        let count = 0;
        items.forEach(item => {
          const s = new Date(item.startDate);
          const e = new Date(item.endDate);
          if (s <= weekEnd && e >= weekStart) count++;
        });
        if (count > max) max = count;
        weekStart.setDate(weekStart.getDate() + 7);
      }
      return max;
    },

    // Height adjustment based on # of Campaigns
    dynamicItemHeight() {
      const availablePerRow = 45;
      const gap = 2;
      const height = Math.floor((availablePerRow - (this.maxOverlap * gap)) / this.maxOverlap);
      return Math.max(5, Math.min(16, height));
    },
    dynamicItemContentHeight() {
      return (this.dynamicItemHeight) + 'px';
    },
    dynamicItemBorderHeight() {
      return '2px';
    },
    dynamicItemFontSize() {
      const h = this.dynamicItemHeight;
      if (h >= 18) return '0.55rem';
      if (h >= 12) return '0.45rem';
      return '0.4rem';
    }
  },
  watch: {
    loaded(val) {
      if (val) {
        this.hasLoadedOnce = true;
        this.displayYear = this.year;
        this.displayMonth = this.month;
        this.displayCampaigns = this.campaigns;
        this.displayHolidays = this.holidays;
      }
    }
  },
  mounted() {
    if (this.loaded) this.hasLoadedOnce = true;
  },
  methods: {
    //navigation
    onInput(newDate) {
      const y = newDate.getFullYear();
      const m = newDate.getMonth() + 1;
      this.$emit('navigate', { jump: true, year: y, month: m });
    },
    //sends user to that campaign once clicked
    onClickItem(item) {
      this.$emit('selectCampaign', item.originalItem.id);
    },
    //used for tooltip info
    onItemMouseenter(item, event) {
      const campaign = this.campaigns.find(c => c.id === item.originalItem.id);
      if (!campaign) return;
      const end = campaign.end_date ? new Date(campaign.end_date) : null;
      const daysLeft = end ? Math.max(0, Math.ceil((end - new Date()) / 86400000)) : null;
      const pct = campaign.donation_target > 0 ? Math.min(Math.round(((campaign.all_time_contribution_total || 0) / campaign.donation_target) * 100), 100) : null;
      this.hoveredCampaign = { ...campaign, daysLeft, pct, color: this.colors[this.campaignColorMap[campaign.id]] };
      this.tooltipPos = { x: event.clientX, y: event.clientY };
    },
    //hides tooltip
    onItemMouseleave() {
      this.hoveredCampaign = null;
    },
    //date formating
    fmtDate(d) {
      return d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    }
  }
};
</script>

<template>
  <div class="calendar-content">
    <div
      v-if="error"
      class="fail-state"
    >
      <i class="fa-solid fa-triangle-exclamation text-amber-400" />
      <p>Unable to load calendar</p>
    </div>

    <template v-else-if="!hasLoadedOnce">
      <div class="cal-skel-container">
        <div
          v-for="i in 6"
          :key="i"
          class="skel cal-skel-row"
        />
      </div>
    </template>

    <template v-else>
      <div
        class="calendar-wrapper"
        :style="{...calendarCssVars, '--item-font': maxOverlap > 3 ? '0px' : dynamicItemFontSize, '--item-height': dynamicItemContentHeight, '--week-height': weekRowHeight}"
      >
        <CalendarView
          :key="displayYear + '-' + displayMonth"
          :show-date="showDate"
          :items="calendarItems"
          :enable-drag-drop="false"
          :enable-date-selection="false"
          current-period-label="Today"
          :item-content-height="dynamicItemContentHeight"
          :item-border-height="dynamicItemBorderHeight"
          item-top="20px"
          :date-classes="holidayDateClasses"
          class="theme-default"
          :do-emit-item-mouse-events="true"
          :enable-tooltip="false"
          @click-item="onClickItem"
          @item-mouseenter="onItemMouseenter"
          @item-mouseleave="onItemMouseleave"
        >
          <template #header="{ headerProps }">
            <CalendarViewHeader
              :header-props="headerProps"
              @input="onInput"
            />
          </template>
          <template #day-content="{ day }">
            <span
              v-if="holidayMap[day.toISOString().slice(0,10)]"
              class="cal-holiday-name"
            >
              {{ holidayMap[day.toISOString().slice(0,10)] }}
            </span>
          </template>
        </CalendarView>
      </div>

      <div
        v-if="calendarItems.length"
        class="cal-legend"
      >
        <div
          v-for="(c, i) in calendarItems.slice(0, 2)"
          :key="c.id"
          class="cal-legend-item"
          @click="$emit('selectCampaign', c.id)"
        >
          <span
            class="cal-legend-dot"
            :style="{ backgroundColor: colors[i] }"
          />
          <span class="cal-legend-name">{{ c.title }}</span>
        </div>
        <span
          v-if="calendarItems.length > 2"
          class="cal-legend-more"
        >+{{ calendarItems.length - 2 }} more</span>
      </div>

      <!-- Hover tooltip -->
      <Teleport to="body">
        <div
          v-if="hoveredCampaign"
          class="cal-tooltip"
          :style="{ left: tooltipPos.x + 12 + 'px', top: tooltipPos.y - 10 + 'px' }"
        >
          <div class="tt-name">
            <span
              class="tt-dot"
              :style="{ backgroundColor: hoveredCampaign.color }"
            />{{ hoveredCampaign.name }}
          </div>
          <div class="tt-row">
            <span class="tt-label">Start</span><span>{{ fmtDate(hoveredCampaign.start_date) }}</span>
          </div>
          <div class="tt-row">
            <span class="tt-label">End</span><span>{{ fmtDate(hoveredCampaign.end_date) }}</span>
          </div>
          <div
            v-if="hoveredCampaign.pct !== null"
            class="tt-row"
          >
            <span class="tt-label">Progress</span><span>{{ hoveredCampaign.pct }}%</span>
          </div>
          <div
            v-if="hoveredCampaign.daysLeft !== null"
            class="tt-row"
          >
            <span class="tt-label">Remaining</span><span>{{ hoveredCampaign.daysLeft }} days</span>
          </div>
        </div>
      </Teleport>
    </template>
  </div>
</template>

<style>
@import "vue-simple-calendar/dist/vue-simple-calendar.css";
@import "vue-simple-calendar/dist/css/default.css";
@import "vue-simple-calendar/dist/css/holidays-us.css";

.cal-tooltip {
  position: fixed; z-index: 9999; background: #1f2937; color: white; border-radius: 8px;
  padding: 10px 12px; font-size: 0.75rem; box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  pointer-events: none; min-width: 180px;
}
.tt-name { font-weight: 700; margin-bottom: 6px; display: flex; align-items: center; gap: 6px; }
.tt-dot { width: 8px; height: 8px; border-radius: 2px; flex-shrink: 0; }
.tt-row { display: flex; justify-content: space-between; padding: 1px 0; }
.tt-label { color: white; }
</style>

<style lang="scss" scoped>
.calendar-content { display: flex; flex-direction: column; flex: 1; }
.fail-state { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; color: #9ca3af; font-size: 0.8rem; gap: 4px; }

.calendar-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* Legend */
.cal-legend { display: flex; flex-wrap: nowrap; gap: 10px; margin-top: 4px; padding-top: 4px; padding-bottom: 8px; border-top: 1px solid rgba(8, 18, 37, 0.08); overflow: hidden; align-items: center; }
.cal-legend-item { display: flex; align-items: center; gap: 4px; font-size: 0.7rem; color: #374151; cursor: pointer; flex-shrink: 0; &:hover { text-decoration: underline; } }
.cal-legend-dot { width: 12px; height: 8px; border-radius: 2px; flex-shrink: 0; }
.cal-legend-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 80px; }
.cal-legend-more { font-size: 0.65rem; color: #6b7280; flex-shrink: 0; }

.cal-skel-container { display: flex; flex-direction: column; gap: 8px; flex: 1; padding: 16px 0; }
.cal-skel-row { height: 40px; border-radius: 4px; }
.skel { background: #f3f4f6; animation: shimmer 1.5s ease-in-out infinite; }
@keyframes shimmer { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }

@media print {
  .calendar-wrapper {
    overflow: visible !important;
    min-height: auto !important;
  }
}
</style>

/* Campaign bar colors */
<style>
.campaign-color-0 { background-color: var(--campaign-color-0) !important; color: white !important; }
.campaign-color-1 { background-color: var(--campaign-color-1) !important; color: white !important; }
.campaign-color-2 { background-color: var(--campaign-color-2) !important; color: white !important; }
.campaign-color-3 { background-color: var(--campaign-color-3) !important; color: white !important; }
.campaign-color-4 { background-color: var(--campaign-color-4) !important; color: white !important; }
.campaign-color-5 { background-color: var(--campaign-color-5) !important; color: white !important; }

/* Header overrides */
.theme-default .cv-header,
.theme-default .cv-header-day {
  background-color: white;
  font-family: inherit;
}
.theme-default .cv-header .periodLabel {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
}
.theme-default .cv-header button {
  color: #6b7280;
  font-size: 0.85rem;
  border-radius: 6px;
  padding: 4px 10px;
}
.theme-default .cv-header button:hover {
  background: #f3f4f6;
}
.theme-default .cv-header-day {
  font-size: 0.65rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
}

/* Item (campaign bar) overrides */
.theme-default .cv-item {
  margin-top: 0;
  height: var(--item-height, 18px);
  font-size: var(--item-font, 0.6rem);
  line-height: var(--item-height, 18px);
  border-radius: 3px;
  padding: 0 4px !important;
  border: none !important;
  color: white;
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.theme-default .cv-item.continued::before,
.theme-default .cv-item.toBeContinued::after {
  display: none;
}

.cal-holiday { background-color: #fefce8 !important; }
.cal-holiday-name { font-size: 0.5rem; color: #92400e; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; position: absolute; right: 2px; top: 2px; font-weight: 600; max-width: calc(100% - 20px); }

/* Today highlight */
.theme-default .cv-day.today {
  background-color: #ecfdf5 !important;
  border: 1px solid #10be5a !important;
}
.theme-default .cv-day.today .cv-day-number {
  color: #10be5a !important;
  font-weight: 700 !important;
}

/* Day cell height and scroll */
.theme-default .cv-week {
  flex: 1 !important;
  min-height: var(--week-height, 5em);
  overflow: hidden;
}

/* Fit calendar within container */
.cv-wrapper {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.cv-weeks {
  flex: 1;
  display: flex !important;
  flex-direction: column !important;
  min-height: 0;
  overflow: hidden;
}
.cv-weekdays {
  overflow: hidden !important;
}
@media screen and (max-width: 430px) {
  .theme-default .cv-header .periodLabel { font-size: 0.8rem; }
  .theme-default .cv-header button { font-size: 0.7rem; padding: 2px 6px; }
  .theme-default .cv-header-day { font-size: 0.5rem; }
  .theme-default .cv-day-number { font-size: 0.6rem; }
  .theme-default .cv-item { font-size: 0px; padding: 0 2px !important; }
  .cal-holiday-name { display: none; }
}

/* Print view adjustments */
@media print {
  .cv-wrapper {
    overflow: visible !important;
    min-height: auto !important;
  }
  .cv-weeks {
    display: flex !important;
    flex-direction: column !important;
    overflow: visible !important;
  }
  .theme-default .cv-week {
    display: flex !important;
    min-height: 4em !important;
    overflow: visible !important;
  }
  .cv-weekdays {
    display: flex !important;
    overflow: visible !important;
  }
  .cv-weekdays .cv-day,
  .cv-week .cv-day {
    flex: 1 1 14.285% !important;
    min-width: 0 !important;
    overflow: hidden !important;
  }
  .theme-default .cv-header button {
    display: none !important;
  }
  .cal-tooltip {
    display: none !important;
  }
}
</style>


